import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ADMIN_GROUP, CANCEL } from "../../../../shared/constants/Constants";
import Breadcrumbs from "../../../../shared/components/Breadcrumbs";
import DefaultButton from "../../../../shared/components/DefaultButton";
import { Row, Col } from "react-bootstrap";
import ListView from "../../../../shared/components/listview/ListView";
import Modalpopup from "../../../../shared/components/Modalpopup";
import JobTitlesTable from "./JobTitlesTable";
import {
  loadAdminGroupAddDataRequest,
  loadAdminGroupDeleteDataRequest,
  loadAdminGroupGetAllRequest,
  loadAdminGroupGetByIdRequest,
  loadAdminGroupUpdateDataRequest,
  resetAdminGroupUpdateData,
  loadAdminGroupUpdateFlagRequest,
  resetAdminGroupUpdateFlag
} from "../redux/Actions";
import { loadJobTitlesDataRequest } from "../../../jobtitles/redux/Actions";
import Select from "react-select";

const AdminGroup = () => {
  const dispatch = useDispatch();
  const { adminGroups = [], groupDetails, updateSuccess, updateFlagSuccess } = useSelector((state) => state.AdminGroup);
  const { jobtitles } = useSelector((store) => store.jobtitle );
  const jobTitleList = Array.isArray(jobtitles) ? jobtitles : jobtitles?.data || [];
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  //const groupOptions = adminGroups.map((g) => ({  label: g.group_name,  value: g.group_id,}));
  console.log("jobTitleList", jobtitles);

 // ── Detail modal ────────────────────────────────────────────────────────────
  const [selectedRow, setSelectedRow] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupType, setNewGroupType] = useState("admin");
  const [selectedJobTitleIds, setSelectedJobTitleIds] = useState([]);

  // ── Admin flags state ───────────────────────────────────────────────────────
  const [isReporting, setIsReporting] = useState(false);
  const [isTicketing, setIsTicketing] = useState(false);

  // ── Inline add form ─────────────────────────────────────────────────────────
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [newJobTitle, setNewJobTitle] = useState("");
  const [addError, setAddError] = useState("");

  // ── Confirm delete modal ────────────────────────────────────────────────────
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingDeleteJobTitleId, setPendingDeleteJobTitleId] = useState(null);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleRowClick = (event) => {
    const row = event?.data;
    if (!row) return;
    setSelectedRow(row);
    setIsReporting(row.is_reporting || false);
    setIsTicketing(row.is_ticketing || false);
    setShowDetailModal(true);
    setShowAddForm(false);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedRow(null);
    setShowAddForm(false);
  };

  const handleDeleteJobTitle = (jobTitleId) => {
    setPendingDeleteJobTitleId(jobTitleId);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    dispatch(
      loadAdminGroupUpdateDataRequest({
        id: selectedRow.group_id,
        group_name: selectedRow.group_name,
        group_type: selectedRow.group_type,
        remove_job_title_ids: [pendingDeleteJobTitleId],
      })
    );
    setShowConfirmModal(false);
    setPendingDeleteJobTitleId(null);
  };


  const handleSubmitAdminFlags = () => {
    // Prepare the payload for updateAdminFlag API
    const payload = {
      group_id: selectedRow.group_id,
      is_reporting: isReporting,
      is_ticketing: isTicketing,
    };

    // Dispatch the update flag request
    dispatch(loadAdminGroupUpdateFlagRequest(payload));
  };

  // ── Job titles filtered by selected group ──────────────────────────────────
  const filteredJobTitles = useMemo(() => {
    if (!jobTitleList || !Array.isArray(jobTitleList)) return [];
    return jobTitleList.filter((jt) => jt.is_active);
  }, [jobTitleList]);

  const handleToggleAddForm = () => {
    setNewGroupName("");
    setNewGroupType("admin");
    setSelectedJobTitleIds([]);
    setAddError("");
    setShowAddForm((prev) => !prev);
  };

  const handleJobTitleSelect = (e) => {
    const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
    setSelectedJobTitleIds(selected);
  };

  const handleAddGroup = () => {
    if (selectedJobTitleIds.length === 0) { 
      setAddError("Please select at least one job title."); 
      return; 
    }

    // ── Check if any selected job title already exists in the group ──
    const existingJobTitleIds = (groupDetails?.job_titles ?? []).map((jt) => jt.job_title_id);
    const alreadyExists = selectedJobTitleIds.some((id) => existingJobTitleIds.includes(Number(id)));

    if (alreadyExists) {
      const duplicateNames = filteredJobTitles
        .filter((jt) => selectedJobTitleIds.includes(jt.id) && existingJobTitleIds.includes(jt.id))
        .map((jt) => jt.job_title)
        .join(", ");
      setAddError(`Already in group: ${duplicateNames}`);
      return;
    }

    dispatch(
      loadAdminGroupUpdateDataRequest({
        id: selectedRow.group_id,
        group_name: selectedRow.group_name,
        group_type: selectedRow.group_type,
        add_job_title_ids: selectedJobTitleIds.map(Number),
      })
    );
    setNewGroupName("");
    setNewGroupType("admin");
    setSelectedJobTitleIds([]);
    setAddError("");
    setShowAddForm(false);
  };

  // ── Data fetching ───────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(loadAdminGroupGetAllRequest());
    dispatch(loadJobTitlesDataRequest());
  }, [dispatch]);

  useEffect(() => {
    if (selectedRow?.group_id) {
      dispatch(loadAdminGroupGetByIdRequest(selectedRow.group_id));
    }
  }, [selectedRow?.group_id]);

  useEffect(() => {
    if (updateSuccess && selectedRow?.group_id) {
      dispatch(loadAdminGroupGetByIdRequest(selectedRow.group_id));
      dispatch(resetAdminGroupUpdateData());
      setShowSuccessAlert(true);                      // ← show alert
      setTimeout(() => setShowSuccessAlert(false), 3000); // ← auto-hide after 3s
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (updateFlagSuccess) {
      // Update the selected row with new flag values instead of reloading entire table
      if (selectedRow) {
        setSelectedRow({
          ...selectedRow,
          is_reporting: isReporting,
          is_ticketing: isTicketing,
        });
      }
      dispatch(resetAdminGroupUpdateFlag());
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3000);
    }
  }, [updateFlagSuccess]);

  // ── Derived data ────────────────────────────────────────────────────────────
  const transformedData = useMemo(() => {
    return adminGroups.map((g) => {
      const employees = [
        ...new Map(
          (g.job_titles ?? [])
            .flatMap((jt) => jt.employees ?? [])
            .map((emp) => [emp.employee_id, emp])
        ).values(),
      ];

      return {
        ...g,
        id: g.group_id,
        job_titles_display:
          g.job_titles?.map((jt) => jt.job_title).join(", ") || "—",
        employees_display:
          employees.map((emp) => `${emp.employee_name} (${emp.employee_id})`).join(", ") || "—",
      };
    });
  }, [adminGroups]);

  const groupedByJobTitle = useMemo(() => {
    const jobTitles = groupDetails?.job_titles ?? [];
    return jobTitles.reduce((acc, jt) => {
      acc[jt.job_title_id] = {
        label: jt.job_title,
        employees: jt.employees ?? [],
      };
      return acc;
    }, {});
  }, [groupDetails]);

  const columnDefs = [
    { display_name: "id", field: "group_id", type: "number", hide: true, pinned: "left" },
    { display_name: "Group Name", field: "group_name", type: "text", width: 200, pinned: "left" },
    { display_name: "Job Titles", field: "job_titles_display", type: "text", width: 400 },
    { display_name: "Employees", field: "employees_display", type: "text", width: 400, hide: true },
    { display_name: "Report", field: "is_reporting", type: "checkbox", width: 120 },
    { display_name: "Ticket", field: "is_ticketing", type: "checkbox", width: 120 },
  ];

  return (
    <>
      <Row className="detailsPageHeader">
        <Col>
          <Breadcrumbs
            children={[{ href: "tickets/admingroup/", label: ADMIN_GROUP }]}
          />
        </Col>
      </Row>

      <ListView
        columnNames={columnDefs}
        sideBarColumnNames={columnDefs.filter(col => col.field !== "group_id")}
        rowData={transformedData}
        rowClick={handleRowClick}
      />

      {/* ── DETAIL MODAL ──────────────────────────────────────────────────── */}
      <Modalpopup
        show={showDetailModal}
        handleClose={handleCloseDetailModal}
        actionTitle={selectedRow?.group_name || "Group Details"}
        size="lg"
        height="500px"
      >
        {showSuccessAlert && (
          <div
            className="alert alert-success alert-dismissible fade show py-2 px-3 mb-3"
            role="alert"
            style={{ fontSize: 13 }}
          >
            <i className="bx bx-check-circle me-1" />
            Group updated successfully!
            <button
              type="button"
              className="btn-close"
              style={{ fontSize: 11 }}
              onClick={() => setShowSuccessAlert(false)}
            />
          </div>
        )}

        {/* Admin Flags Section */}
        <div className="border rounded p-3 mb-3">
          <div className="row align-items-center">
            <div className="col-md-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="isReporting"
                  checked={isReporting}
                  onChange={(e) => setIsReporting(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="isReporting">
                  Is Reporting
                </label>
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="isTicketing"
                  checked={isTicketing}
                  onChange={(e) => setIsTicketing(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="isTicketing">
                  Is Ticketing
                </label>
              </div>
            </div>
            <div className="col-md-4 text-end">
              <DefaultButton
                buttonClass="default-btn-color btn btn-sm"
                buttonTitle="Save"
                actionBlock={handleSubmitAdminFlags}
              />
            </div>
          </div>
        </div>

        {/* Add Job Title button */}
        <div className="d-flex justify-content-end mb-3">
          <DefaultButton
            buttonClass="default-btn-color btn btn-sm"
            buttonTitle={showAddForm ? "Cancel" : "+ Add Job Title"}
            actionBlock={handleToggleAddForm}
          />
        </div>

        {/* Inline add form */}
        {showAddForm && (
          <div className="border rounded p-3 mb-3 bg-light">
            <div className="d-flex gap-2 align-items-start flex-wrap">
              <div style={{ minWidth: 220, flex: 1 }}>
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                  Job Titles
                </label>
                <Select
                  isMulti
                  options={filteredJobTitles.map((jt) => ({
                    label: jt.job_title,
                    value: jt.id,
                  }))}
                  value={filteredJobTitles
                    .filter((jt) => selectedJobTitleIds.includes(jt.id))
                    .map((jt) => ({
                      label: jt.job_title,
                      value: jt.id,
                    }))
                  }
                  onChange={(selectedOptions) => {
                    const values = selectedOptions.map((opt) => opt.value);
                    setSelectedJobTitleIds(values);
                  }}
                  className="basic-multi-select"
                  classNamePrefix="select"
                />
              </div>

              <div className="d-flex align-items-end" style={{ paddingTop: 22 }}>
                <DefaultButton
                  buttonClass="default-btn-color btn btn-sm"
                  buttonTitle="Add"
                  actionBlock={handleAddGroup}
                />
              </div>
            </div>
            {addError && <p className="text-danger mb-0 mt-2" style={{ fontSize: 13 }}>{addError}</p>}
          </div>
        )}

        {/* Table */}
        <JobTitlesTable
          groupedByJobTitle={groupedByJobTitle}
          onDelete={handleDeleteJobTitle}
        />

        <div className="col-md-12 mt-3 d-flex gap-2">
          <DefaultButton
            buttonClass="btn btn-secondary"
            buttonTitle={CANCEL}
            actionBlock={handleCloseDetailModal}
          />
        </div>
      </Modalpopup>

      {/* ── CONFIRM DELETE MODAL ──────────────────────────────────────────── */}
      <Modalpopup
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        actionTitle="Confirm Delete"
        size="sm"
      >
        <p className="mb-4">Are you sure you want to delete this job title? This action cannot be undone.</p>
        <div className="d-flex gap-2">
          <DefaultButton
            buttonClass="btn btn-danger"
            buttonTitle="Delete"
            actionBlock={handleConfirmDelete}
          />
          <DefaultButton
            buttonClass="btn btn-secondary"
            buttonTitle={CANCEL}
            actionBlock={() => setShowConfirmModal(false)}
          />
        </div>
      </Modalpopup>
    </>
  );
};

export default AdminGroup;